import { useState, useCallback } from "react";
import {
  verifyFederationAffiliation,
  lookupFederationAthlete,
  VerifyResult,
  LookupResult,
} from "../services/federationApi";

export type FederationStatus =
  | "idle"
  | "loading"
  | "verified"
  | "not_found"
  | "not_active"
  | "expired"
  | "error";

interface UseFederationVerificationReturn {
  status: FederationStatus;
  result: VerifyResult | null;
  lookupData: LookupResult | null;
  errorMessage: string | null;
  verify: (
    federationSlug: string,
    cpf: string,
    fighterId?: number,
    token?: string | null,
    force?: boolean,
    checkEmail?: string
  ) => Promise<VerifyResult | null>;
  lookup: (
    federationSlug: string,
    cpf: string,
    token?: string | null
  ) => Promise<LookupResult | null>;
  reset: () => void;
}

const useFederationVerification = (): UseFederationVerificationReturn => {
  const [status, setStatus] = useState<FederationStatus>("idle");
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [lookupData, setLookupData] = useState<LookupResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const verify = useCallback(
    async (
      federationSlug: string,
      cpf: string,
      fighterId?: number,
      token?: string | null,
      force?: boolean,
      checkEmail?: string
    ): Promise<VerifyResult | null> => {
      setStatus("loading");
      setErrorMessage(null);

      try {
        const res = await verifyFederationAffiliation(
          federationSlug,
          cpf,
          fighterId,
          token ?? null,
          force,
          checkEmail
        );

        setResult(res);

        if (res.verified) {
          setStatus("verified");
        } else if (res.reason === "NOT_FOUND") {
          setStatus("not_found");
          setErrorMessage("Atleta não encontrado nesta federação.");
        } else if (res.reason === "EXPIRED") {
          setStatus("expired");
          setErrorMessage("Filiação expirada.");
        } else {
          setStatus("not_active");
          setErrorMessage(res.message || "Filiação não está ativa.");
        }

        return res;
      } catch (err: any) {
        setStatus("error");
        if (err?.response?.status === 401) {
          setErrorMessage("Sessão expirada. Faça login novamente para continuar.");
        } else {
          setErrorMessage(
            err?.response?.data?.message ||
              "Erro ao verificar filiação. Tente novamente."
          );
        }
        return null;
      }
    },
    []
  );

  const lookup = useCallback(
    async (
      federationSlug: string,
      cpf: string,
      token?: string | null
    ): Promise<LookupResult | null> => {
      setStatus("loading");
      setErrorMessage(null);

      try {
        const res = await lookupFederationAthlete(
          federationSlug,
          cpf,
          token ?? null
        );
        setLookupData(res);

        if (res.found) {
          setStatus("verified");
        } else {
          setStatus("not_found");
          setErrorMessage("Atleta não encontrado nesta federação.");
        }

        return res;
      } catch (err: any) {
        setStatus("error");
        if (err?.response?.status === 401) {
          setErrorMessage("Sessão expirada. Faça login novamente para continuar.");
        } else {
          setErrorMessage(
            err?.response?.data?.message ||
              "Erro ao consultar federação. Tente novamente."
          );
        }
        return null;
      }
    },
    []
  );

  const reset = useCallback(() => {
    setStatus("idle");
    setResult(null);
    setLookupData(null);
    setErrorMessage(null);
  }, []);

  return { status, result, lookupData, errorMessage, verify, lookup, reset };
};

export default useFederationVerification;
